import type { KnowledgeItem } from '@/types';

// 港机码头知识库（12 条）
// 覆盖：工艺基础 / 关键参数 / 三大故障处置 / 设备维护 / 安全规程
export const portcraneKnowledge: KnowledgeItem[] = [
  // ========== 工艺基础 ==========
  {
    id: 'PC-K001',
    tags: ['港机', '工艺', '基础'],
    question: '集装箱桥式起重机（岸桥）的基本原理是什么？',
    answer:
      '岸桥是码头集装箱装卸的核心设备，由大梁（臂架）、小车、起升机构、吊具、大车走行组成。' +
      '作业时大车沿轨道对位，小车沿大梁水平运行到集装箱上方，起升机构下放吊具，' +
      '吊具扭锁咬合集装箱四角后起升，再由小车平移到大船/堆场侧。' +
      '超巴拿马型岸桥前伸距 60~70m，起升高度 40~50m，额定载荷 40~65t。',
  },
  {
    id: 'PC-K002',
    tags: ['起升机构', '关键参数'],
    question: '起升机构的关键参数有哪些？',
    answer:
      '起升速度正常 30~60m/min，额定载荷 ≤65t，起升高度 0~50m。' +
      '重载时应降速到 20m/min 减钢丝绳冲击。载荷百分比 LOAD-PCT = 实际载荷 / 65t × 100%，超 100% 即超载。' +
      '起升电机为变频驱动，载荷越大电流越大，钢丝绳张力也越大。',
  },
  {
    id: 'PC-K003',
    tags: ['小车', '跑偏', '关键参数'],
    question: '小车运行跑偏量为什么重要？',
    answer:
      '小车沿大梁轨道水平运行，跑偏量正常 < 20mm。跑偏超标通常是轨道一侧磨损或导向轮间隙过大，' +
      '会导致导向轮偏磨加剧，严重时小车脱轨。跑偏量 TROLLEY-DEV 持续上升时必须先降速再纠偏，' +
      '直接纠偏会加剧导向轮冲击。',
  },
  {
    id: 'PC-K004',
    tags: ['大车', '锚定', '关键参数'],
    question: '大车走行和锚定装置的作用是什么？',
    answer:
      '大车走行驱使整机沿码头轨道移动对位，速度正常 0~30m/min。制动状态 0=释放、1=制动。' +
      '锚定装置是抗风最后一道保险——大风时制动器 BRAKE 置 1 后还要插锚定销，' +
      '因为制动器长时间承受风载会打滑失效，锚定销直接卡死轨道才能抗住阵风。',
  },
  {
    id: 'PC-K005',
    tags: ['吊具', '扭锁', '关键参数'],
    question: '吊具扭锁和对箱是怎么操作的？',
    answer:
      '吊具状态 0=待机、1=锁定、2=松开。对箱时吊具四角扭锁对准集装箱角件，' +
      '下放到位后扭锁旋转 90° 咬合（STATUS=1）才能起升。吊具扭角正常 < 2°，' +
      '扭角过大说明吊具未对正，强行起升会损坏角件。起升速度过快会甩箱，急停更会脱锁。',
  },

  // ========== 典型故障处置 ==========
  {
    id: 'PC-K101',
    tags: ['起升', '超载', '故障'],
    question: '起升载荷达到 72t（超载 10%）怎么办？',
    answer:
      '这是起升机构超载故障。正确操作：①观察载荷传感器读数确认超载 ②起升降速到 20m/min ' +
      '③将吊具下降到安全高度 ④核查集装箱实际重量 ⑤确认 LOAD-PCT < 100% 通关。' +
      '⚠️ 绝对不能：直接急停 —— 集装箱摆动可能脱锁。铁律是先降速减冲击，再核查重量。',
  },
  {
    id: 'PC-K102',
    tags: ['小车', '跑偏', '高难故障'],
    question: '小车跑偏量达 35mm（正常<20mm）怎么处理？',
    answer:
      '这是小车跑偏故障。正确操作：①观察小车跑偏量和轨道偏差 ②小车降速到 15m/min ' +
      '③微调小车纠偏（TROLLEY 工艺按钮）④观察跑偏量回落 ⑤确认 TROLLEY-DEV < 20 通关。' +
      '⚠️ 绝对不能：不降速直接纠偏 —— 导向轮冲击可能加剧，严重时脱轨。',
  },
  {
    id: 'PC-K103',
    tags: ['大风', '风报警', '极危故障'],
    question: '风速达到 28m/s（超报警阈值 25m/s）怎么办？',
    answer:
      '⚠️ 极危故障"大车风报警"——必须立即制动并锚定。正确操作：①观察风速仪确认报警 ' +
      '②大车急停制动（BRAKE=1）③大梁俯仰收起到 60° 减风载 ④部署锚定装置 ⑤等 30 秒观察风速 ' +
      '⑥确认 BRAKE=1 且 WIND-SPD<25 通关。⚠️ 绝对不能：先收臂不制动 —— 起重机被风吹跑。' +
      '铁律：先制动、再收臂、最后锚定。',
  },
  {
    id: 'PC-K104',
    tags: ['风速', '阈值', '安全'],
    question: '港机作业的风速限制是多少？',
    answer:
      '风速正常 < 20m/s 可正常作业，> 20m/s 限制作业（降速减载），> 25m/s 报警必须制动锚定，' +
      '> 32m/s 紧急停机。老师傅的行话叫"风大不跑"——阵风来之前就要收臂锚定，' +
      '不能等风到了再动车。风速仪要定期校准，结冰会导致误报低风速。',
  },

  // ========== 设备维护 ==========
  {
    id: 'PC-K201',
    tags: ['钢丝绳', '维护'],
    question: '起升钢丝绳怎么维护？',
    answer:
      '钢丝绳是港机头号耗材，维护重点：①每班目视检查断丝、锈蚀、变形 ' +
      '②每月测定润滑状态，定期涂专用钢丝绳脂 ③断丝率超 10% 或直径磨损超 7% 必须更换 ' +
      '④超载会加速断丝，重载降速到 20m/min 可减冲击。起升载荷越大钢丝绳张力越大，' +
      '超载断丝是港机最常见的事故源。',
  },
  {
    id: 'PC-K202',
    tags: ['载荷传感器', '校准', '维护'],
    question: '载荷传感器漂移怎么排查？',
    answer:
      '载荷传感器每季度校准一次。超载告警时先看 LOAD-PCT 趋势：' +
      '若趋势平稳只是数值偏高，可能是传感器漂移；若趋势持续上升，是真超载。' +
      '排查方法：用标准砝码比对实际载荷 LOAD-ACT，偏差 > 3% 需重新标定。' +
      '漂移会导致误报超载或漏报真超载，两种都很危险。',
  },

  // ========== 安全规程 ==========
  {
    id: 'PC-K301',
    tags: ['安全规程', '大风', '超载'],
    question: '港机安全规程的核心口诀是什么？',
    answer:
      '老师傅八个字：风大不跑、超载不吊。具体三条铁律：' +
      '①大风先停后做——风速 > 25m/s 必须先制动再收臂最后锚定，顺序不能乱；' +
      '②超载先降后查——超载必须先降速减冲击再核查重量，绝不能直接急停甩箱；' +
      '③跑偏先慢后纠——跑偏必须先降速减偏磨再纠偏，直接纠偏加剧导向轮冲击。' +
      '港机是铁疙瘩，急不得，任何处置都遵循"先停后做"的安全原则。',
  },
];
